import React, { useState, useEffect } from 'react';
import { Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { projectsAPI, workTypesAPI } from '../../helpers/api';
import SitesListModal from './SitesListModal';
import SiteDetailModal from './SiteDetailModal';
import ProjectVehiclesModal from './ProjectVehiclesModal';
import ProjectPersonnelModal from './ProjectPersonnelModal';
import ProjectWaybillsModal from './ProjectWaybillsModal';
import ProjectCostModal from './ProjectCostModal';

interface Site {
  id: string;
  name: string;
  code: string;
  address: string | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
}

interface Project {
  id: string;
  name: string;
  code: string;
  description: string | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  city: { id: string; name: string; code?: string; parentId?: string | null };
  sites: Site[];
  createdAt: string;
  _count?: {
    sites: number;
    workItems: number;
    projectVehicles: number;
    projectPersonnel: number;
  };
}

interface City {
  id: string;
  name: string;
  code?: string;
  parentId?: string | null;
}

interface District {
  id: string;
  name: string;
}

interface ProjectDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  project: Project | null;
  cities: City[];
  onUpdate?: () => void;
}

const statusStyles: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-10 text-success',
  COMPLETED: 'bg-info bg-opacity-10 text-info',
  SUSPENDED: 'bg-warning bg-opacity-10 text-warning',
  CANCELLED: 'bg-danger bg-opacity-10 text-danger',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Aktif',
  COMPLETED: 'Tamamlandı',
  SUSPENDED: 'Askıda',
  CANCELLED: 'İptal',
};

const statusOptions = [
  { value: 'ACTIVE', label: 'Aktif' },
  { value: 'COMPLETED', label: 'Tamamlandı' },
  { value: 'SUSPENDED', label: 'Askıda' },
  { value: 'CANCELLED', label: 'İptal' },
];

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({
  isOpen,
  toggle,
  project,
  cities,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [districts, setDistricts] = useState<District[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    provinceId: '',
    districtId: '',
    status: '',
    startDate: '',
    endDate: '',
  });

  // Sites list modal state
  const [sitesModalOpen, setSitesModalOpen] = useState(false);
  // Site detail modal state
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [siteDetailModalOpen, setSiteDetailModalOpen] = useState(false);
  // Project-level modals
  const [vehiclesModalOpen, setVehiclesModalOpen] = useState(false);
  const [personnelModalOpen, setPersonnelModalOpen] = useState(false);
  const [waybillsModalOpen, setWaybillsModalOpen] = useState(false);
  const [costModalOpen, setCostModalOpen] = useState(false);

  // Proje İşleri state
  const [projectWorks, setProjectWorks] = useState<any[]>([]);
  const [loadingProjectWorks, setLoadingProjectWorks] = useState(false);
  const [unassignedWorkTypes, setUnassignedWorkTypes] = useState<any[]>([]);
  const [showAddWork, setShowAddWork] = useState(false);
  const [assigningWork, setAssigningWork] = useState(false);

  const openSitesModal = () => {
    setSitesModalOpen(true);
  };

  const closeSitesModal = () => {
    setSitesModalOpen(false);
  };

  const openSiteDetail = (site: Site) => {
    setSelectedSite(site);
    setSiteDetailModalOpen(true);
  };

  // Reset form when project changes
  useEffect(() => {
    if (project) {
      // city parentId varsa ilçedir, yoksa ildir
      const city = project.city as City;
      const isDistrict = city?.parentId != null;

      setFormData({
        name: project.name || '',
        code: project.code || '',
        description: project.description || '',
        provinceId: isDistrict ? (city?.parentId || '') : (city?.id || ''),
        districtId: isDistrict ? (city?.id || '') : '',
        status: project.status || '',
        startDate: project.startDate ? project.startDate.split('T')[0] : '',
        endDate: project.endDate ? project.endDate.split('T')[0] : '',
      });
      setIsEditing(false);
      setShowAddWork(false);
      setDistricts([]);
      fetchProjectWorks(project.id);

      // Eğer ilçeyse, ilçeleri yükle
      if (isDistrict && city?.parentId) {
        loadDistricts(city.parentId);
      }
    }
  }, [project]);

  // Proje İşleri fonksiyonları
  const fetchProjectWorks = async (projectId: string) => {
    try {
      setLoadingProjectWorks(true);
      const res = await workTypesAPI.getProjectWorks(projectId);
      setProjectWorks(res.data);
    } catch (error) {
      console.error('Proje işleri yüklenemedi:', error);
    } finally {
      setLoadingProjectWorks(false);
    }
  };

  const fetchUnassignedWorkTypes = async () => {
    if (!project) return;
    try {
      const res = await workTypesAPI.getUnassignedForProject(project.id);
      setUnassignedWorkTypes(res.data);
    } catch (error) {
      console.error('Atanmamış iş türleri yüklenemedi:', error);
    }
  };

  const handleAssignWork = async (workTypeId: string) => {
    if (!project) return;
    try {
      setAssigningWork(true);
      await workTypesAPI.assignToProject(project.id, { workTypeId });
      toast.success('İş türü projeye atandı');
      setShowAddWork(false);
      fetchProjectWorks(project.id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İş türü atanamadı');
    } finally {
      setAssigningWork(false);
    }
  };

  const handleRemoveWork = async (projectWorkId: string) => {
    if (!project) return;
    try {
      await workTypesAPI.removeProjectWork(projectWorkId);
      toast.success('İş türü projeden kaldırıldı');
      fetchProjectWorks(project.id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İş türü kaldırılamadı');
    }
  };

  // İlçeleri yükle
  const loadDistricts = async (provinceId: string) => {
    try {
      setLoadingDistricts(true);
      const res = await projectsAPI.getDistricts(provinceId);
      setDistricts(res.data);
    } catch (error) {
      console.error('İlçeler yüklenemedi:', error);
    } finally {
      setLoadingDistricts(false);
    }
  };

  // İl değiştiğinde ilçeleri yükle
  const handleProvinceChange = async (provinceId: string) => {
    setFormData(prev => ({ ...prev, provinceId, districtId: '' }));
    setDistricts([]);

    if (provinceId) {
      await loadDistricts(provinceId);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!project) return;

    try {
      setSaving(true);
      // İlçe seçildiyse ilçe ID'sini, yoksa il ID'sini gönder
      const cityId = formData.districtId || formData.provinceId;
      await projectsAPI.update(project.id, {
        name: formData.name,
        code: formData.code,
        description: formData.description || null,
        cityId,
        status: formData.status,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
      });
      toast.success('Proje güncellendi');
      setIsEditing(false);
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (project) {
      const city = project.city as City;
      const isDistrict = city?.parentId != null;

      setFormData({
        name: project.name || '',
        code: project.code || '',
        description: project.description || '',
        provinceId: isDistrict ? (city?.parentId || '') : (city?.id || ''),
        districtId: isDistrict ? (city?.id || '') : '',
        status: project.status || '',
        startDate: project.startDate ? project.startDate.split('T')[0] : '',
        endDate: project.endDate ? project.endDate.split('T')[0] : '',
      });

      // İlçeleri tekrar yükle
      if (isDistrict && city?.parentId) {
        loadDistricts(city.parentId);
      } else {
        setDistricts([]);
      }
    }
    setIsEditing(false);
  };

  if (!project) return null;

  const handleDelete = async () => {
    if (!project) return;
    if (!window.confirm('Bu projeyi silmek istediğinizden emin misiniz?')) return;
    try {
      await projectsAPI.delete(project.id);
      toast.success('Proje silindi');
      toggle();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  const headerActions = (
    <>
      {!isEditing ? (
        <>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={() => setIsEditing(true)}
            title="Düzenle"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleDelete}
            title="Sil"
            style={{ color: '#f06548' }}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleSave}
            disabled={saving}
            title="Kaydet"
            style={{ color: saving ? '#adb5bd' : '#0ab39c' }}
          >
            {saving ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
          </button>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleCancel}
            disabled={saving}
            title="İptal"
          >
            <i className="ri-close-line"></i>
          </button>
        </>
      )}
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Proje Detayı"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
    >
      <div className="project-detail-content" style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* ─── Card: Header + Details ─── */}
        <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}>
          {/* Header */}
          <div className="pb-3 mb-3" style={{ borderBottom: '1px solid #f2f4f7' }}>
            <div className="d-flex align-items-start justify-content-between">
              <div style={{ flex: 1, marginRight: '1rem' }}>
                {isEditing ? (
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-control-lg mb-2"
                    style={{ fontWeight: 600 }}
                  />
                ) : (
                  <h4 className="mb-1" style={{ fontWeight: 600 }}>
                    {project.name}
                  </h4>
                )}
                {isEditing ? (
                  <Input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="Proje Kodu"
                    bsSize="sm"
                    style={{ width: '150px' }}
                  />
                ) : (
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    {project.code}
                  </p>
                )}
              </div>
              {isEditing ? (
                <Input
                  type="select"
                  name="status"
                  value={formData.status}
                  onChange={handleSelectChange}
                  bsSize="sm"
                  style={{ width: 'auto' }}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              ) : (
                <span
                  className={`px-2 py-1 rounded-pill small ${statusStyles[project.status]}`}
                  style={{ fontWeight: 500 }}
                >
                  {statusLabels[project.status]}
                </span>
              )}
            </div>
          </div>

          {/* Details Grid */}
          <div>
            <div className="text-muted small mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              Detaylar
            </div>
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
            {/* İl */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">İl</span>
              {isEditing ? (
                <Input
                  type="select"
                  name="provinceId"
                  value={formData.provinceId}
                  onChange={(e) => handleProvinceChange(e.target.value)}
                  bsSize="sm"
                  style={{ width: '180px' }}
                >
                  <option value="">İl Seçin</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.code ? `${city.code} - ${city.name}` : city.name}
                    </option>
                  ))}
                </Input>
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {(() => {
                    const city = project.city as City;
                    if (city?.parentId) {
                      // İlçeyse, il adını bulmaya çalış
                      const province = cities.find(c => c.id === city.parentId);
                      return province?.name || '-';
                    }
                    return city?.name || '-';
                  })()}
                </span>
              )}
            </div>
            {/* İlçe */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">İlçe</span>
              {isEditing ? (
                <Input
                  type="select"
                  name="districtId"
                  value={formData.districtId}
                  onChange={handleSelectChange}
                  bsSize="sm"
                  style={{ width: '180px' }}
                  disabled={!formData.provinceId || loadingDistricts}
                >
                  <option value="">
                    {loadingDistricts ? 'Yükleniyor...' : 'İlçe Seçin (Opsiyonel)'}
                  </option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </Input>
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {(() => {
                    const city = project.city as City;
                    // parentId varsa bu bir ilçedir
                    return city?.parentId ? city.name : '-';
                  })()}
                </span>
              )}
            </div>
            {/* Başlangıç Tarihi */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Başlangıç Tarihi</span>
              {isEditing ? (
                <Input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '180px' }}
                />
              ) : (
                <span style={{ fontWeight: 500 }}>{formatDate(project.startDate)}</span>
              )}
            </div>
            {/* Bitiş Tarihi */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Bitiş Tarihi</span>
              {isEditing ? (
                <Input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '180px' }}
                />
              ) : (
                <span style={{ fontWeight: 500 }}>{formatDate(project.endDate)}</span>
              )}
            </div>
            {/* Kayıt Tarihi */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Kayıt Tarihi</span>
              <span>{formatDate(project.createdAt)}</span>
            </div>
            {/* Şantiye Sayısı */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Şantiye Sayısı</span>
              <span style={{ fontWeight: 500 }}>{project._count?.sites || project.sites?.length || 0}</span>
            </div>
            {/* Araç Sayısı */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Araç Sayısı</span>
              <span style={{ fontWeight: 500 }}>{project._count?.projectVehicles || 0}</span>
            </div>
            {/* Personel Sayısı */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2">
              <span className="text-muted">Personel Sayısı</span>
              <span style={{ fontWeight: 500 }}>{project._count?.projectPersonnel || 0}</span>
            </div>
          </div>
        </div>

          {/* Description */}
          {(isEditing || project.description) && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f2f4f7' }}>
              <div className="text-muted small mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                Açıklama
              </div>
              {isEditing ? (
                <Input
                  type="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Proje açıklaması..."
                />
              ) : (
                <p className="mb-0 small" style={{ color: '#495057' }}>
                  {project.description}
                </p>
              )}
            </div>
          )}
        </div>{/* ─── End Card: Header + Details ─── */}

        {/* ─── Card: Sites ─── */}
        {project.sites && project.sites.length > 0 && (
          <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #0ab39c' }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                Şantiyeler
              </span>
              <span className="text-muted small">{project.sites.length} adet</span>
            </div>
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              {project.sites.slice(0, 3).map((site, idx, arr) => (
                <div
                  key={site.id}
                  className="d-flex align-items-center justify-content-between px-3 py-2"
                  style={{
                    borderBottom: idx < arr.length - 1 ? '1px solid #eaecf0' : 'none',
                    cursor: 'pointer',
                  }}
                  onClick={() => openSiteDetail(site)}
                >
                  <div>
                    <span style={{ fontWeight: 500 }}>{site.name}</span>
                    {site.code && (
                      <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                        ({site.code})
                      </span>
                    )}
                  </div>
                  <i className="ri-arrow-right-s-line text-muted"></i>
                </div>
              ))}
              {project.sites.length > 3 && (
                <div
                  className="px-3 py-2 text-center text-primary"
                  style={{ cursor: 'pointer', fontWeight: 500, fontSize: '0.9rem' }}
                  onClick={openSitesModal}
                >
                  +{project.sites.length - 3} şantiye daha
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Card: İşler ─── */}
        <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }}>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-2"
                style={{ width: 28, height: 28, background: '#fff4de', color: '#b76e00' }}
              >
                <i className="ri-tools-line" style={{ fontSize: '0.9rem' }}></i>
              </span>
              <span className="small" style={{ fontWeight: 600, color: '#344054' }}>
                Proje İşleri
                {projectWorks.length > 0 && (
                  <span className="ms-1 text-muted" style={{ fontWeight: 400 }}>({projectWorks.length})</span>
                )}
              </span>
            </div>
            {showAddWork ? (
              <button
                type="button"
                className="btn btn-sm px-2 py-0"
                style={{ fontSize: '0.75rem', color: '#667085', border: '1px solid #e4e7ec', borderRadius: '6px', lineHeight: '24px' }}
                onClick={() => setShowAddWork(false)}
              >
                Vazgec
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm px-2 py-0"
                style={{ fontSize: '0.75rem', color: '#b76e00', background: '#fff4de', border: '1px solid #f0dfc0', borderRadius: '6px', lineHeight: '24px' }}
                onClick={() => { fetchUnassignedWorkTypes(); setShowAddWork(true); }}
              >
                <i className="ri-add-circle-line me-1" style={{ fontSize: '0.7rem' }}></i>
                İş Türü Ekle
              </button>
            )}
          </div>

          {showAddWork && (
            <div className="rounded-3 p-3 mb-3" style={{ background: '#f8f9fb', border: '1px dashed #d0d5dd', maxHeight: '220px', overflowY: 'auto' }}>
              {unassignedWorkTypes.length === 0 ? (
                <p className="text-muted small mb-0 text-center">Atanabilecek iş türü bulunamadı</p>
              ) : (
                unassignedWorkTypes.map((wt: any) => (
                  <button
                    key={wt.id}
                    type="button"
                    className="btn btn-sm w-100 mb-1 text-start d-flex align-items-center gap-2"
                    style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: '8px' }}
                    onClick={() => handleAssignWork(wt.id)}
                    disabled={assigningWork}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-2"
                      style={{ width: 24, height: 24, background: '#fff4de', color: '#b76e00', fontSize: '0.7rem', flexShrink: 0 }}
                    >
                      <i className="ri-tools-line"></i>
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>{wt.name}</span>
                      <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>({wt.unit})</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {loadingProjectWorks ? (
            <div className="text-center py-2"><Spinner size="sm" color="primary" /></div>
          ) : projectWorks.length === 0 ? (
            <div className="text-center py-3 rounded-3" style={{ background: '#f8f9fb' }}>
              <p className="text-muted mb-0 small">Henüz iş türü atanmamış</p>
            </div>
          ) : (
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              {projectWorks.map((pw: any, idx: number) => (
                <div
                  key={pw.id}
                  className="d-flex align-items-center justify-content-between px-3 py-2 section-list-item"
                  style={{ borderBottom: idx < projectWorks.length - 1 ? '1px solid #f2f4f7' : 'none' }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-2"
                      style={{ width: 28, height: 28, background: '#fff4de', color: '#b76e00', fontSize: '0.7rem', flexShrink: 0 }}
                    >
                      <i className="ri-tools-line"></i>
                    </span>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{pw.workType.name}</div>
                      <small className="text-muted">
                        {pw.workType.unit}
                        {pw.targetQuantity && ` · Hedef: ${Number(pw.targetQuantity).toLocaleString('tr-TR')}`}
                      </small>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm p-0 section-remove-btn"
                    onClick={() => handleRemoveWork(pw.id)}
                    title="Projeden Kaldır"
                    style={{ color: '#98a2b3', transition: 'color 0.15s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#f04438')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#98a2b3')}
                  >
                    <i className="ri-close-line" style={{ fontSize: '1.1rem' }}></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>{/* ─── End Card: İşler ─── */}

        {/* ─── Card: Hızlı Erişim ─── */}
        <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }}>
          <div className="text-muted small mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            Hızlı Erişim
          </div>
          <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
              onClick={openSitesModal}
            >
              <div className="d-flex align-items-center">
                <i className="ri-building-line me-2 text-primary"></i>
                <span style={{ fontWeight: 500 }}>Şantiyeler</span>
              </div>
              <div className="d-flex align-items-center">
                <span className="text-muted small me-2">{project._count?.sites || project.sites?.length || 0}</span>
                <i className="ri-arrow-right-s-line text-muted"></i>
              </div>
            </div>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
              onClick={() => setVehiclesModalOpen(true)}
            >
              <div className="d-flex align-items-center">
                <i className="ri-truck-line me-2 text-danger"></i>
                <span style={{ fontWeight: 500 }}>Araçlar</span>
              </div>
              <div className="d-flex align-items-center">
                <span className="text-muted small me-2">{project._count?.projectVehicles || 0}</span>
                <i className="ri-arrow-right-s-line text-muted"></i>
              </div>
            </div>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
              onClick={() => setPersonnelModalOpen(true)}
            >
              <div className="d-flex align-items-center">
                <i className="ri-user-line me-2 text-warning"></i>
                <span style={{ fontWeight: 500 }}>Personel</span>
              </div>
              <div className="d-flex align-items-center">
                <span className="text-muted small me-2">{project._count?.projectPersonnel || 0}</span>
                <i className="ri-arrow-right-s-line text-muted"></i>
              </div>
            </div>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
              onClick={() => setWaybillsModalOpen(true)}
            >
              <div className="d-flex align-items-center">
                <i className="ri-file-list-3-line me-2 text-info"></i>
                <span style={{ fontWeight: 500 }}>İrsaliyeler</span>
              </div>
              <div className="d-flex align-items-center">
                <i className="ri-arrow-right-s-line text-muted"></i>
              </div>
            </div>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ cursor: 'pointer' }}
              onClick={() => setCostModalOpen(true)}
            >
              <div className="d-flex align-items-center">
                <i className="ri-money-dollar-circle-line me-2 text-success"></i>
                <span style={{ fontWeight: 500 }}>Maliyet</span>
              </div>
              <div className="d-flex align-items-center">
                <i className="ri-arrow-right-s-line text-muted"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sites List Modal */}
      <SitesListModal
        isOpen={sitesModalOpen}
        toggle={closeSitesModal}
        project={project ? { id: project.id, name: project.name, code: project.code, city: project.city } : null}
        onUpdate={onUpdate}
      />

      {/* Site Detail Modal */}
      <SiteDetailModal
        isOpen={siteDetailModalOpen}
        toggle={() => setSiteDetailModalOpen(false)}
        site={selectedSite ? {
          ...selectedSite,
          project: {
            id: project.id,
            name: project.name,
            code: project.code,
            city: project.city,
          },
        } : null}
        onUpdate={onUpdate}
      />

      {/* Project Vehicles Modal */}
      <ProjectVehiclesModal
        isOpen={vehiclesModalOpen}
        toggle={() => setVehiclesModalOpen(false)}
        project={{ id: project.id, name: project.name, code: project.code, city: project.city }}
        onUpdate={onUpdate}
      />

      {/* Project Personnel Modal */}
      <ProjectPersonnelModal
        isOpen={personnelModalOpen}
        toggle={() => setPersonnelModalOpen(false)}
        project={{ id: project.id, name: project.name, code: project.code, city: project.city }}
        onUpdate={onUpdate}
      />

      {/* Project Waybills Modal */}
      <ProjectWaybillsModal
        isOpen={waybillsModalOpen}
        toggle={() => setWaybillsModalOpen(false)}
        project={{ id: project.id, name: project.name, code: project.code, city: project.city }}
        onUpdate={onUpdate}
      />

      {/* Project Cost Modal */}
      <ProjectCostModal
        isOpen={costModalOpen}
        toggle={() => setCostModalOpen(false)}
        project={{ id: project.id, name: project.name, code: project.code, city: project.city }}
      />
    </SideModal>
  );
};

export default ProjectDetailModal;
